import { FC } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  AvatarList,
  AvatarVariant,
  IndicatorsList,
  Widget,
} from '@factorialco/factorial-one-react/dist/experimental'
import { HolidaysHomeWidgetDocument, HolidaysHomeWidgetQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { NEW_HOME_WIDGETS, NEW_HOME_WIDGET_ACTION_TYPES } from 'modules/dashboard/lib/events'

const MAX_AVATARS = 9

const useLinkAndTitle = () => {
  const title = i18n.t('new_dashboard.holidays.title')

  const subtitle = dateToString(moment(), 'ui_dm_long')

  const { navigation } = useNavigation()

  const linkUrl = navigation.calendar.fullPath()

  const handleUpperRightActionClick = () => {
    track(trackingEvents.dashboard.clickOnHomeWidgetAction, {
      widget: NEW_HOME_WIDGETS.HOLIDAYS,
      actionType: NEW_HOME_WIDGET_ACTION_TYPES.UPPER_RIGHT_BUTTON,
    })
  }

  return {
    title,
    subtitle,
    link: {
      url: linkUrl,
      title,
      onClick: handleUpperRightActionClick,
    },
  }
}

const prepareData = (data: HolidaysHomeWidgetQuery) => {
  const count = data.timeoff.upcomingLeavesConnection?.totalCount ?? 0

  const indicatorListItems = [
    {
      content: `${count}`,
      emoji: '🏝️',
      label: i18n.tp('new_dashboard.holidays.on_holidays', {
        count,
      }),
    },
  ]

  const avatars: AvatarVariant[] = data.timeoff.upcomingLeavesConnection.nodes
    .map((leave) => {
      const employee = leave.employee

      if (!employee) return

      return {
        firstName: employee.preferredName ?? employee.firstName,
        lastName: employee.lastName,
        src: employee.access.avatar ? buildUrl(employee.access.avatar) : undefined,
        type: 'person' as const,
      }
    })
    .slice(0, MAX_AVATARS)
    .filter(Boolean)

  let remainingCount: number | undefined = Math.max(0, count - MAX_AVATARS)
  remainingCount = remainingCount === 0 ? undefined : remainingCount

  const isEmpty = !count

  return {
    isEmpty,
    indicatorListItems,
    avatars,
    remainingCount,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const HolidaysWidgetComponent: FC<{ data: PreparedData; employeeId: number }> = ({
  data: { indicatorListItems, avatars, remainingCount },
}) => {
  const { link, title, subtitle } = useLinkAndTitle()

  return (
    <Widget
      header={{
        title,
        subtitle,
        link,
      }}
      fullHeight
    >
      <Box height="full" justifyContent="spaceBetween">
        <IndicatorsList items={indicatorListItems} />
        <Box>
          <AvatarList
            avatars={avatars}
            type="person"
            layout="fill"
            remainingCount={remainingCount}
            noTooltip
          />
        </Box>
      </Box>
    </Widget>
  )
}

export const buildHolidaysWidget = () =>
  buildWidget({
    id: 'holidays',
    document: HolidaysHomeWidgetDocument,
    isEmpty: ({ isEmpty }) => isEmpty,
    prepareData,
    component: HolidaysWidgetComponent,
  })
